import {
  SVButton,
  SVForm,
  SVLabeledText,
  SVLoader,
  SVPhoneInput,
  SVTextInput,
} from '@/components/core';
import { useUpdatePhoneNumber } from '@/hooks/requests/useAuth';
import { Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import showToast from '@/utils/toast';
import { MESSAGES } from '@/constants/messages';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/types/user';
import { useGetUser } from '@/hooks/requests/useUser';
import { formatPhoneNumber } from '@/utils/phoneNumber';
import { UpdateProfileSettingsFormsProps } from '@/types/auth';
import Update2FAModal from './Update2FAModal';

// NOTE: This is phone number on server side, but specified as mobile number in the UI
type UpdateMobileNumberFields = {
  phoneNumber: string;
  confirmPhoneNumber: string;
  code: string;
};

export default function UpdatePhoneNumberModal({
  open,
  setOpen,
}: UpdateProfileSettingsFormsProps) {
  const { user } = useGetUser();
  const [open2FAModal, setOpen2FAModal] = useState(false);
  const [isOnOtpStep, setIsOnOtpStep] = useState(false);
  const {
    startUpdatePhoneNumber,
    startUpdatePhoneNumberLoading,
    updatePhoneNumber,
    updatePhoneNumberLoading,
  } = useUpdatePhoneNumber();
  const queryClient = useQueryClient();

  const formMethods = useForm<UpdateMobileNumberFields>({
    defaultValues: {
      phoneNumber: '',
      confirmPhoneNumber: '',
      code: '',
    },
  });

  async function handleEmailStep() {
    const data = formMethods.getValues();
    try {
      await startUpdatePhoneNumber({ phoneNumber: data.phoneNumber });
      setIsOnOtpStep(true);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  async function handleSubmit(data: UpdateMobileNumberFields) {
    if (!isOnOtpStep) {
      setOpen2FAModal(true);
      return;
    }

    try {
      await updatePhoneNumber({
        phoneNumber: data.phoneNumber,
        otp: data.code,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.detail,
      });
      showToast({
        type: 'success',
        data: { content: 'Your mobile number has been successfully updated' },
      });
      setOpen(false);
    } catch {
      showToast({ type: 'error', data: { content: MESSAGES.genericError } });
    }
  }

  return (
    <Dialog fullWidth open={open} onClose={() => setOpen(false)}>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="h5" component="h1">
            Update Your Mobile Number
          </Typography>
          <Typography>
            {isOnOtpStep
              ? 'Please enter the one time password that was sent to your new mobile number'
              : "For your security, use a mobile number as your primary contact. We'll use it to verify your identity or alert you to suspicious activity."}
          </Typography>
          {!isOnOtpStep && (
            <SVLabeledText
              label="Current Mobile Number"
              text={formatPhoneNumber(user?.phoneNumber || '')}
            />
          )}
          <SVForm<UpdateMobileNumberFields>
            methods={formMethods}
            onSubmit={handleSubmit}>
            {startUpdatePhoneNumberLoading ? (
              <SVLoader />
            ) : (
              <>
                {isOnOtpStep ? (
                  <SVTextInput
                    name="code"
                    label="OTP Code"
                    htmlInputProps={{ maxLength: 6 }}
                    rules={{ required: true }}
                  />
                ) : (
                  <>
                    <SVPhoneInput
                      name="phoneNumber"
                      label="New Mobile Number"
                    />
                    <SVPhoneInput
                      name="confirmPhoneNumber"
                      label="Confirm Mobile Number"
                      validateMatch="phoneNumber"
                    />
                  </>
                )}
              </>
            )}
          </SVForm>
          {isOnOtpStep && (
            <SVButton
              variant="outlined"
              isLoading={startUpdatePhoneNumberLoading}
              onClick={handleEmailStep}>
              Resend Code
            </SVButton>
          )}
          <SVButton
            isLoading={updatePhoneNumberLoading}
            onClick={formMethods.handleSubmit(handleSubmit)}
            type="submit">
            Submit
          </SVButton>
          <Update2FAModal
            open={open2FAModal}
            setOpen={setOpen2FAModal}
            afterSuccessCallback={handleEmailStep}
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
